import { Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PaymentChaseType } from '../enums/payment-chase-type.enum';

/**
 * The three payment types, with the names and examples a supplier sees (MP-11244). On phones the cards become a
 * three-way switch (MP-11329), so each also has a `shortName` short enough for a third of a small screen (it may
 * wrap onto two lines).
 */
export const PAYMENT_TYPE_CARDS: readonly { type: PaymentChaseType; name: string; shortName: string; example: string }[] = [
  {
    type: PaymentChaseType.PERCENTAGE_DEPOSIT,
    name: 'Percentage deposit, then the rest',
    shortName: '% deposit',
    example: 'e.g. 25% now, the rest later'
  },
  { type: PaymentChaseType.FLAT_FEE_DEPOSIT, name: 'Fixed deposit, then the rest', shortName: '£ deposit', example: 'e.g. £500 now, the rest later' },
  { type: PaymentChaseType.FULL_PAYMENT, name: 'Single payment', shortName: 'Single payment', example: 'e.g. no deposit, all paid at once' }
];

/**
 * Chooses how a job is paid from three cards (a three-way switch on phones, MP-11329), in place of the payment-type
 * dropdown (MP-11244). Used by Payment
 * settings, Payment setup on the job and Payment on the booking form, so all three read the same. A form control:
 * its value is a {@link PaymentChaseType}, or `''` when nothing is chosen (in Payment settings, "No default").
 * Behaves as a radio group: arrow keys move the choice and Home/End jump to the first/last card, as with native
 * radios. Pass `labelledBy` the id of the visible question so screen readers announce it as the group's name.
 */
@Component({
  standalone: true,
  selector: 'payment-type-cards',
  templateUrl: './payment-type-cards.component.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PaymentTypeCardsComponent), multi: true }]
})
export class PaymentTypeCardsComponent implements ControlValueAccessor {
  readonly cards = PAYMENT_TYPE_CARDS;

  /** The id of the visible question that names this group, e.g. "How is Hannah paying?". */
  readonly labelledBy = input<string | null>(null);

  /** The chosen type, or `''` for none. */
  readonly value = signal<string>('');
  readonly disabled = signal(false);

  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  choose(type: PaymentChaseType): void {
    if (this.disabled() || this.value() === type) return;
    this.value.set(type);
    this._onChange(type);
    this._onTouched();
  }

  /**
   * Left/up and right/down move the choice to the previous or next card, wrapping round; Home and End jump to the
   * first and last — like native radios.
   */
  onKeydown(event: KeyboardEvent, index: number, buttons: HTMLElement): void {
    const last = this.cards.length - 1;
    const targets: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowDown: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      ArrowUp: index === 0 ? last : index - 1,
      Home: 0,
      End: last
    };
    const next = targets[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.choose(this.cards[next].type);
    (buttons.querySelectorAll('[role="radio"]')[next] as HTMLElement | undefined)?.focus();
  }

  /** The chosen type's example, shown under the switch on phones; null when nothing is chosen. */
  get chosenExample(): string | null {
    return this.cards.find(card => card.type === this.value())?.example ?? null;
  }

  /** Only the chosen card is in the tab order; with none chosen, the first is, as with native radios. */
  tabIndexFor(type: PaymentChaseType, index: number): number {
    const chosen = this.cards.some(card => card.type === this.value());
    return (chosen ? this.value() === type : index === 0) ? 0 : -1;
  }
}
